import { useLayoutEffect, useRef, type RefObject } from 'react';
import { cardZoomNow } from '../utils/cardZoom';

interface Options {
    /** The field never grows the node past this height in px; beyond it the text scrolls. */
    max?: number;
    /**
     * Called with the height (px) the field is short by after a USER edit, so the host node
     * can grow by that much. Not called for programmatic changes.
     */
    onUserGrow?: (deltaPx: number) => void;
}

/**
 * For a prompt field that fills the room its node leaves it. When typing makes the text
 * taller than the field, the node grows by the difference (until the field is `max` tall)
 * so the text stays in view. The node never shrinks by itself. Call `markUserEdit()` from
 * the onChange handler. While the canvas is zoomed out and the cards' insides are zoomed up
 * for reading (utils/cardZoom.ts) the node keeps its size and the text scrolls in the field.
 */
export const useAutoGrowTextarea = (ref: RefObject<HTMLTextAreaElement>, value: string, { max = 360, onUserGrow }: Options = {}) => {
    const userEdit = useRef(false);
    const onUserGrowRef = useRef(onUserGrow);
    onUserGrowRef.current = onUserGrow;

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        // Only a user's edit grows the node; measuring otherwise would force a layout of a node body the browser is skipping off screen
        if (!userEdit.current) return;
        // Zoomed up for reading, a node keeps its size (and the field's sizes are in zoomed units)
        if (cardZoomNow() !== 1) { userEdit.current = false; return; }
        const box = el.clientHeight;
        const text = el.scrollHeight;
        if (userEdit.current && box > 0 && box < max && text > box + 1) {
            onUserGrowRef.current?.(Math.min(text, max) - box);
        }
        userEdit.current = false;
    }, [value, max, ref]);

    return { markUserEdit: () => { userEdit.current = true; } };
};
